/* AgentMeeting — Marketing-layer top bar (for landing/auth/pricing/docs/changelog) */
import React from 'react';
import { useI18n, LangSwitch } from './i18n';
import { useAuth } from './auth';

export type GoToFn = (route: string) => void;

export function MarketingBar({ here, goTo, signedIn: _signedIn }: { here?: string; goTo: GoToFn; signedIn?: boolean }) {
  const { t } = useI18n();
  const { user, signOut } = useAuth();
  const isSignedIn = user != null;
  const [menuOpen, setMenuOpen] = React.useState(false);
  const pillRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (pillRef.current && !pillRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [menuOpen]);

  const tabs = [
    { id: 'features',     label: t('nav.features'),    target: 'landing#features' },
    { id: 'how-it-works', label: t('nav.howItWorks'),  target: 'landing#how' },
    { id: 'use-cases',    label: t('nav.useCases'),    target: 'landing#cases' },
    { id: 'pricing',      label: t('nav.pricing'),     target: 'pricing' },
  ];

  const onClick = (tab: typeof tabs[number]) => {
    if (tab.target.includes('#')) {
      const [page, anchor] = tab.target.split('#');
      goTo(page);
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    } else {
      goTo(tab.target);
    }
  };

  const displayName = user ? (user.name || user.email) : '';
  const initials = displayName.slice(0, 2).toUpperCase();

  return (
    <div className="mkt-bar">
      <div className="mkt-bar-inner">
        <div className="flex center gap-2" onClick={() => goTo('landing')} style={{ cursor: 'pointer' }}>
          <LogoMark size={20} />
          <span className="mono" style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em' }}>AgentMeeting</span>
        </div>

        <nav className="flex center gap-5 mono" style={{ fontSize: 12 }}>
          {tabs.map(tab => (
            <span key={tab.id} onClick={() => onClick(tab)}
              style={{
                cursor: 'pointer',
                color: here === tab.id || here === tab.target ? 'var(--fg-0)' : 'var(--fg-2)',
                fontWeight: here === tab.id ? 500 : 400,
                whiteSpace: 'nowrap',
              }}>
              {tab.label}
            </span>
          ))}
        </nav>

        <div className="flex center gap-3">
          <ThemeSwitch />
          <LangSwitch />
          {isSignedIn ? (
            <>
              <button className="btn ghost mono" onClick={() => goTo('app/agenda')} style={{ whiteSpace: 'nowrap' }}>
                {t('cta.openApp')}
              </button>
              <div ref={pillRef} style={{ position: 'relative' }}>
                <div className="flex center gap-2" onClick={() => setMenuOpen(o => !o)}
                  style={{
                    padding: '4px 10px 4px 4px', border: '1px solid var(--line-2)',
                    borderRadius: 100, cursor: 'pointer',
                  }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: 'var(--live)', color: 'var(--bg-0)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 600,
                  }}>{initials}</div>
                  <span className="mono tiny">{displayName}</span>
                </div>
                {menuOpen && (
                  <div className="mono" style={{
                    position: 'absolute', top: 36, right: 0, whiteSpace: 'nowrap',
                    padding: 6, border: '1px solid var(--line-2)', borderRadius: 6,
                    background: 'var(--bg-1)', fontSize: 11,
                    display: 'flex', flexDirection: 'column', gap: 2, zIndex: 50,
                  }}>
                    <div
                      onClick={() => { setMenuOpen(false); goTo('settings'); }}
                      style={{ padding: '4px 8px', cursor: 'pointer' }}
                    >
                      Settings
                    </div>
                    <div
                      onClick={() => { setMenuOpen(false); signOut(); goTo('landing'); }}
                      style={{ padding: '4px 8px', cursor: 'pointer' }}
                    >
                      Sign out
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button className="btn ghost mono" onClick={() => goTo('signin')} style={{ whiteSpace: 'nowrap' }}>
                {t('cta.signin')}
              </button>
              <button className="btn primary mono" onClick={() => goTo('signup')} style={{ whiteSpace: 'nowrap' }}>
                {t('cta.startFree')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ThemeSwitch() {
  const [theme, setTheme] = React.useState<'dark' | 'light'>(() => {
    try {
      const raw = localStorage.getItem('agentmeeting.prefs');
      const p = raw ? JSON.parse(raw) : {};
      return p.theme === 'light' ? 'light' : 'dark';
    } catch { return 'dark'; }
  });
  React.useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      const raw = localStorage.getItem('agentmeeting.prefs');
      const p = raw ? JSON.parse(raw) : {};
      p.theme = theme;
      localStorage.setItem('agentmeeting.prefs', JSON.stringify(p));
    } catch { /* noop */ }
  }, [theme]);
  return (
    <button
      onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
      title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
      style={{
        appearance: 'none', border: '1px solid var(--line-2)', background: 'transparent',
        borderRadius: 100, padding: '3px 9px', cursor: 'pointer',
        color: 'var(--fg-2)', fontFamily: 'var(--mono)', fontSize: 11,
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}>
      {theme === 'dark' ? '☾' : '☀'} <span>{theme === 'dark' ? 'DARK' : 'LIGHT'}</span>
    </button>
  );
}

export function LogoMark({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="6"  cy="9"  r="2.6" fill="var(--pro)" />
      <circle cx="18" cy="9"  r="2.6" fill="var(--err)" />
      <circle cx="12" cy="18" r="2.6" fill="var(--judge)" />
      <path d="M6 9 L18 9 M6 9 L12 18 M18 9 L12 18"
        stroke="var(--fg-3)" strokeWidth="0.8" strokeDasharray="1.5 1.5" />
    </svg>
  );
}

export function MarketingFooter({ goTo }: { goTo: GoToFn }) {
  const { t, lang } = useI18n();
  return (
    <footer className="mkt-footer">
      <div className="mkt-container">
        <div className="mkt-footer-grid">
          <div className="col gap-3" style={{ minWidth: 180 }}>
            <div className="flex center gap-2">
              <LogoMark size={18} />
              <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>AgentMeeting</span>
            </div>
            <div className="meta" style={{ lineHeight: 1.6, fontSize: 11 }}>
              {lang === 'zh'
                ? '一个能被多个 AI 同时拷打的工作台。'
                : 'A workstation that can be cross-examined by multiple AIs at once.'}
            </div>
            <div className="mono tiny dim">v0.4.2 · public beta</div>
          </div>

          <FootCol title={t('foot.product')}>
            <li onClick={() => goTo('landing')}>{lang === 'zh' ? '首页' : 'Home'}</li>
            <li onClick={() => goTo('pricing')}>{t('nav.pricing')}</li>
            <li onClick={() => goTo('app/agenda')}>{lang === 'zh' ? '打开应用' : 'Open app'}</li>
            <li onClick={() => goTo('signup')}>{lang === 'zh' ? '注册' : 'Sign up'}</li>
          </FootCol>

          <FootCol title={t('foot.resources')}>
            <li onClick={() => goTo('docs')}>{t('nav.docs')}</li>
            <li onClick={() => goTo('changelog')}>{t('nav.changelog')}</li>
            <li onClick={() => goTo('docs#api')}>API {lang === 'zh' ? '参考' : 'reference'}</li>
            <li>{lang === 'zh' ? '状态页' : 'Status'} ↗</li>
          </FootCol>

          <FootCol title={t('foot.company')}>
            <li>{lang === 'zh' ? '关于' : 'About'}</li>
            <li>{lang === 'zh' ? '博客' : 'Blog'}</li>
            <li>{lang === 'zh' ? '招聘' : 'Careers'}</li>
            <li>contact@agentmeeting.dev</li>
          </FootCol>

          <FootCol title={t('foot.legal')}>
            <li>{lang === 'zh' ? '隐私政策' : 'Privacy'}</li>
            <li>{lang === 'zh' ? '服务条款' : 'Terms'}</li>
            <li>{lang === 'zh' ? '安全' : 'Security'}</li>
            <li>DPA</li>
          </FootCol>
        </div>

        <div className="flex between" style={{
          paddingTop: 28, marginTop: 36, borderTop: '1px solid var(--line-1)',
          fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--fg-3)',
          flexWrap: 'wrap', gap: 12,
        }}>
          <span>{t('foot.copy')}</span>
          <div className="flex center gap-3">
            <span style={{ cursor: 'pointer' }}>X / Twitter ↗</span>
            <span style={{ cursor: 'pointer' }}>GitHub ↗</span>
            <span style={{ cursor: 'pointer' }}>Discord ↗</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function FootCol({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="col gap-2">
      <div className="mono" style={{
        fontSize: 10, color: 'var(--fg-3)',
        letterSpacing: '0.14em', textTransform: 'uppercase',
      }}>{title}</div>
      <ul style={{
        listStyle: 'none', padding: 0, margin: 0,
        display: 'flex', flexDirection: 'column', gap: 8,
        fontSize: 12, color: 'var(--fg-1)',
      }}>{children}</ul>
    </div>
  );
}
